var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import ElementController from "../base-controllers/ElementController.js";
import { value } from "../base-controllers/element_controller/value_decorator.js";
import { gettext } from "../utils/i18n.js";
import { getOrPresentMessage } from "../global-controllers/messages/utils.js";
import ActionSubmission from "./action/action_submission.js";
import { RequestMethod } from "../../utils/http/index.js";
import { getFormController } from "../components/form/lookup.js";
import { FormEvent } from "../components/form/constants.js";
import { setStaticAttr, removeStaticAttr } from "../utils/morph/helpers.js";
/** Interpret only the real unsaved main image; keep live edits outside its preview. */
class PhotoSuggestions extends ElementController {
    constructor() {
        super(...arguments);
        this.generation = 0;
        this.busy = false;
        this.uploadFailed = false;
        this.previewing = false;
        this.requestedRevision = "";
        this.requestedHash = "";
        this.progressWanted = false;
        this.preserveEdit = (event) => this.preserveField(event.currentTarget);
        this.interpret = async (event) => {
            event.preventDefault();
            const file = this.input?.files?.[0];
            if (this.busy || this.button?.disabled || !file)
                return;
            await this.submitImage(file);
        };
    }
    init() {
        if (this.autoInterpret && this.root.dataset.photoState === "pending") {
            this.waiting = { hash: this.sourceHash, preserved: new Set() };
        }
        this.onGlobal("change", (event) => {
            if (event.currentTarget !== this.input)
                return;
            const file = this.input?.files?.[0];
            if (this.autoInterpret && file && file !== this.waitingFile) {
                // Start protection synchronously, before hashing or a busy upload ends.
                const preserved = this.waitingFile ? new Set() : this.waiting?.preserved ?? new Set();
                this.waiting = { hash: "", preserved };
                this.waitingFile = file;
            }
            if (!file)
                this.waiting = undefined;
            void this.refresh();
        }, { delegate: 'input[type="file"]' });
        for (const eventName of ["input", "change"]) {
            this.onGlobal(eventName, this.preserveEdit, { delegate: "[name]" });
        }
        if (this.form)
            this.on(this.form, FormEvent.sendEnd, () => this.deferRefresh());
        void this.refresh();
    }
    async destroy() {
        clearTimeout(this.refreshTimer);
        await this.syncProgress(false);
        this.suggestionsToast?.dismiss();
        await super.destroy();
    }
    sourceHashChanged() { void this.refresh(); }
    revisionChanged() { void this.refresh(); }
    get form() { return this.root.closest("form"); }
    get input() {
        return this.form?.querySelector('[data-image-role="front"] input[type="file"]');
    }
    get button() { return this.root.querySelector("[data-main-image-interpret]"); }
    preserveField(field) {
        const name = field.getAttribute("name") ?? "";
        if (!this.waiting || !this.form?.contains(field))
            return;
        if (/^item_form-(category|colors|desc)$/.test(name))
            this.waiting.preserved.add(name);
        if (name.startsWith("item_category_form-")) {
            // Do not switch away from the layout the user is editing.
            this.waiting.preserved.add("item_form-category");
            if (/^item_category_form-(headrest|has_frame|top_shape)$/.test(name))
                this.waiting.preserved.add(name);
        }
    }
    deferRefresh() {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => { void this.refresh(); }, 50);
    }
    cameraIsOpen() {
        if (!this.autoInterpret || !this.form?.id)
            return false;
        const camera = document.querySelector(`dialog[data-camera-dialog][data-camera-form-id="${CSS.escape(this.form.id)}"][open]`);
        if (!camera)
            return false;
        if (camera !== this.cameraDialog) {
            this.cameraDialog = camera;
            this.on(camera, "close", () => {
                this.cameraDialog = undefined;
                void this.refresh();
            }, { once: true });
        }
        return true;
    }
    async refresh() {
        if (!this.root.isConnected)
            return;
        const generation = ++this.generation;
        const file = this.input?.files?.[0];
        if (this.button)
            this.button.disabled = true;
        this.setProposalsDisabled(true);
        let hash = "";
        if (file) {
            const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
            hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
        }
        if (generation !== this.generation || !this.root.isConnected)
            return;
        if (file && this.waiting?.hash === "") {
            const alreadyRendered = this.form?.querySelector("[data-photo-proposal-revision]")?.dataset.photoProposalRevision === this.root.dataset.fieldProposalsRevision;
            if (hash === this.sourceHash && this.root.dataset.photoState === "ready" && alreadyRendered)
                this.waiting = undefined;
            else
                this.waiting.hash = hash;
        }
        const stale = !!file && hash !== this.sourceHash;
        const pending = this.root.dataset.photoState === "pending";
        const cameraOpen = this.cameraIsOpen();
        if (stale && !cameraOpen)
            setStaticAttr(this.root, "data-photo-client-stale", "");
        else
            removeStaticAttr(this.root, "data-photo-client-stale");
        await this.syncProgress(this.autoInterpret && !!file && !cameraOpen && !this.uploadFailed && (this.busy || (stale && hash !== this.requestedHash) || (!stale && (pending || (this.waiting?.hash === hash && this.root.dataset.photoState === "ready")))));
        if (generation !== this.generation || !this.root.isConnected)
            return;
        if (this.button) {
            this.button.hidden = !file || !stale || (this.autoInterpret && (cameraOpen || this.busy || hash !== this.requestedHash));
            this.button.disabled = !file || !stale || this.busy;
        }
        this.setProposalsDisabled(!file || stale || this.busy || pending);
        const notice = this.root.querySelector("[data-main-image-stale]");
        if (notice) {
            notice.hidden = !stale || cameraOpen;
            notice.setAttribute("role", this.uploadFailed ? "alert" : "status");
            notice.textContent = this.uploadFailed
                ? gettext("The main image could not be interpreted. Your information remains. Try again.")
                : this.autoInterpret ? gettext("Interpreting the new image…")
                    : gettext("Main image changed. Interpret it to update the suggestions.");
        }
        if (file && stale && this.autoInterpret && !cameraOpen && !this.busy && hash !== this.requestedHash) {
            if (this.waiting?.hash !== hash)
                this.waiting = { hash, preserved: new Set() };
            this.waitingFile = file;
            this.requestedHash = hash;
            await this.submitImage(file);
            return;
        }
        if (cameraOpen)
            return;
        const revision = this.root.dataset.fieldProposalsRevision;
        const rendered = this.form?.querySelector("[data-photo-proposal-revision]")?.dataset.photoProposalRevision;
        const controller = this.form && getFormController(this.form);
        if (!file || stale || this.busy || this.previewing || !revision || !controller)
            return;
        if (controller.isRerendering) {
            this.deferRefresh();
            return;
        }
        const round = this.autoInterpret && this.waiting?.hash === hash && this.root.dataset.photoState === "ready" ? this.waiting : undefined;
        // An ordinary field rerender can receive the finished result before its
        // status update. It must finish the progress toast too, without a second
        // preview request or a false announcement when no proposals exist.
        if (round && revision === rendered) {
            this.waiting = undefined;
            await this.notifySuggestions();
            return;
        }
        if (revision === rendered || revision === this.requestedRevision)
            return;
        this.requestedRevision = revision;
        this.previewing = true;
        try {
            let preserved;
            do {
                const active = document.activeElement;
                if (round && active instanceof HTMLElement && this.form?.contains(active)) {
                    this.preserveField(active);
                }
                preserved = JSON.stringify(Array.from(round?.preserved ?? []).sort());
                this.setPreviewIntent(round ? revision : "", preserved);
                await controller.sendForm({ rerender: true });
                // Edits during the actual request also win. The ordinary Form protects
                // their DOM values; retire only those fields' automatic proposal intent.
            } while (round && this.waiting === round && this.root.isConnected && this.sourceHash === hash && this.input?.files?.[0] === file && preserved !== JSON.stringify(Array.from(round.preserved).sort()));
            const latest = this.form?.querySelector("[data-photo-proposal-revision]")?.dataset.photoProposalRevision;
            if (latest !== revision)
                this.requestedRevision = "";
            if (round && latest === revision && this.waiting === round && this.input?.files?.[0] === file && this.sourceHash === hash) {
                this.waiting = undefined;
                await this.notifySuggestions();
            }
        }
        catch {
            this.requestedRevision = "";
            await getOrPresentMessage("error", gettext("The main image could not be interpreted. Your information remains. Try again."));
        }
        finally {
            this.setPreviewIntent("", "");
            this.previewing = false;
            this.deferRefresh();
        }
    }
    setPreviewIntent(revision, preserved) {
        const preview = this.form?.querySelector('[name="image_suggestion_preview"]');
        const protection = this.form?.querySelector('[name="image_suggestion_preserve"]');
        if (preview)
            preview.value = revision;
        if (protection)
            protection.value = preserved;
    }
    async syncProgress(wanted) {
        this.progressWanted = wanted;
        if (!wanted) {
            this.progressToast?.dismiss();
            this.progressToast = undefined;
            return;
        }
        const previous = this.suggestionsToast;
        this.suggestionsToast = undefined;
        if (previous?.isConnected && !previous.classList.contains("toast-dismissing")) {
            previous.querySelector("[data-view-photo-suggestions]")?.remove();
            previous.update(gettext("Interpreting the new image…"), { loading: true });
            previous.setAttribute("data-photo-interpretation-progress", "");
            this.progressToast = previous;
        }
        else
            previous?.dismiss();
        if (this.progressToast || this.progressPromise)
            return this.progressPromise;
        this.progressPromise = getOrPresentMessage("info", gettext("Interpreting the new image…"), {
            duration: 0,
            loading: true,
            htmlAttributes: { tags: this.root.id, "data-photo-interpretation-progress": "", role: "status" },
        }).then(toast => {
            if (!this.progressWanted || !this.root.isConnected)
                toast.dismiss();
            else
                this.progressToast = toast;
        }).finally(() => { this.progressPromise = undefined; });
        await this.progressPromise;
    }
    async notifySuggestions() {
        if (this.progressPromise)
            await this.progressPromise;
        const progress = this.progressToast;
        this.progressToast = undefined;
        await this.syncProgress(false);
        if (!this.form?.querySelector("[data-proposal-field]")) {
            progress?.dismiss();
            return;
        }
        const toast = progress?.isConnected && !progress.classList.contains("toast-dismissing")
            ? progress
            : await getOrPresentMessage("info", gettext("New image suggestions"), { duration: 0, htmlAttributes: { tags: this.root.id, role: "status" } });
        if (!this.root.isConnected) {
            toast.dismiss();
            return;
        }
        toast.removeAttribute("data-photo-interpretation-progress");
        toast.update(gettext("New image suggestions"));
        this.suggestionsToast = toast;
        if (toast.querySelector("[data-view-photo-suggestions]"))
            return;
        const button = document.createElement("button");
        button.type = "button";
        button.dataset.slot = "button";
        button.dataset.variant = "ghost";
        button.dataset.size = "sm";
        button.dataset.viewPhotoSuggestions = "";
        button.textContent = gettext("View suggestions");
        button.addEventListener("click", () => {
            const target = this.form?.querySelector("[data-image-proposal-summary]")
                ?? this.form?.querySelector("[data-proposal-field]");
            // Focus first: hiding a mobile keyboard must not interrupt a smooth
            // scroll that was started against the old visual viewport.
            target?.focus({ preventScroll: true });
            target?.scrollIntoView({ block: "start", behavior: "instant" });
            toast.dismiss();
        });
        button.classList.add("toast-action");
        toast.insertBefore(button, toast.querySelector(".toast-close"));
    }
    setProposalsDisabled(disabled) {
        for (const button of this.root.querySelectorAll('[data-controller="SetFormFieldValue"], [data-photo-interpretation-retry]'))
            button.disabled = disabled;
        const selector = this.autoInterpret ? '[data-proposal-field] [data-controller="SetFormFieldValue"]' : '[data-proposal-field] [data-field-value^="apply:"]';
        for (const button of this.form?.querySelectorAll(selector) ?? [])
            button.disabled = disabled;
    }
    async submitImage(file) {
        this.busy = true;
        this.uploadFailed = false;
        if (this.button)
            this.button.disabled = true;
        const body = new FormData();
        body.append("interpret_main_image", "1");
        body.append("image", file);
        const url = new URL(this.root.dataset.mainImageUrl, location.href);
        body.append("category", this.form?.querySelector('[name="item_form-category"]')?.value ?? url.searchParams.get("category") ?? "furniture");
        body.append("desc", this.form?.querySelector('[name="item_form-desc"]')?.value ?? "");
        const submission = new ActionSubmission({
            indicatorDefinition: "inline",
            indicatorSelfElement: this.button ?? this.root,
        }, RequestMethod.post, this.root.dataset.mainImageUrl, { body });
        try {
            const response = await submission.perform();
            this.uploadFailed = !response?.succeeded;
            if (window.hasPageUpdatesPromise)
                await window.hasPageUpdatesPromise;
        }
        catch {
            this.uploadFailed = true;
            await getOrPresentMessage("error", gettext("The main image could not be interpreted. Your information remains. Try again."));
        }
        finally {
            this.busy = false;
            await this.refresh();
        }
    }
}
PhotoSuggestions.id = "PhotoSuggestions";
PhotoSuggestions.binds = {
    "[data-main-image-interpret]": [["click", "interpret"]],
};
export default PhotoSuggestions;
__decorate([
    value(String)
], PhotoSuggestions.prototype, "sourceHash", void 0);
__decorate([
    value(String)
], PhotoSuggestions.prototype, "revision", void 0);
__decorate([
    value(Boolean)
], PhotoSuggestions.prototype, "autoInterpret", void 0);
//# sourceMappingURL=PhotoSuggestions.js.map